/**
 * 📊 Indicator Types — Inteligência Operacional
 */

export type IndicatorType =
  | 'cycle_time'
  | 'lead_time'
  | 'sla_compliance'
  | 'task_completion'
  | 'process_efficiency'
  | 'project_progress'
  | 'custom';

export type IndicatorDirection = 'higher_is_better' | 'lower_is_better' | 'target_is_best';

export type IndicatorStatus = 'active' | 'paused' | 'archived';

export type IndicatorFrequency = 'realtime' | 'hourly' | 'daily' | 'weekly' | 'monthly';

export interface Indicator {
  id: string;
  organization_id: string;
  name: string;
  description?: string | null;
  type: IndicatorType;
  unit: string;
  target_value?: number | null;
  current_value: number;
  previous_value?: number | null;
  threshold_warning?: number | null;
  threshold_critical?: number | null;
  direction: IndicatorDirection;
  status: IndicatorStatus;
  frequency: IndicatorFrequency;
  workflow_id?: string | null;
  action_id?: string | null;
  process_id?: string | null;
  project_id?: string | null;
  issue_id?: string | null;
  visibility_scope?: 'private' | 'project' | 'organization';
  owner_id?: string | null;
  created_by?: string | null;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

export interface IndicatorSnapshot {
  id: string;
  indicator_id: string;
  organization_id: string;
  value: number;
  recorded_at: string;
}

export const INDICATOR_TYPE_LABELS: Record<IndicatorType, string> = {
  cycle_time: 'Tempo de Ciclo',
  lead_time: 'Lead Time',
  sla_compliance: 'Conformidade SLA',
  task_completion: 'Taxa de Conclusão',
  process_efficiency: 'Eficiência de Processo',
  project_progress: 'Progresso de Projeto',
  custom: 'Personalizado',
};

export const INDICATOR_STATUS_LABELS: Record<IndicatorStatus, string> = {
  active: 'Ativo',
  paused: 'Pausado',
  archived: 'Arquivado',
};

/** Avalia a saúde de um indicador com base nos thresholds */
export function getIndicatorHealth(indicator: Indicator): 'healthy' | 'warning' | 'critical' | 'unknown' {
  const { current_value, target_value, threshold_warning, threshold_critical, direction } = indicator;
  if (target_value == null) return 'unknown';

  const deviation = direction === 'lower_is_better'
    ? current_value - target_value
    : target_value - current_value;

  if (threshold_critical != null && deviation >= threshold_critical) return 'critical';
  if (threshold_warning != null && deviation >= threshold_warning) return 'warning';
  return deviation <= 0 ? 'healthy' : 'warning';
}
